import {useMemo} from 'react';
import {
    Column,
    Container,
    Image,
    Panel,
    Panel9Slice,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {PositionType} from '@wonderlandengine/react-ui';
import {colorSwatch} from '../utils/colorSwatch.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';

import {parseColor} from '../utils/utils.js';
import {ColoredImage} from '../components/colored-image.js';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {MalUI} from '../components/mal-ui.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {MalSpeech, TailDirection} from './mal-speech.js';
import {Localization} from '../../utils/Localization.js';

export const MalRoot = (props: {
    rank;
    xpPercentage;
    additionalRotation?: [number, number, number];
    text: string;
}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const progressMaterial = useMemo(() => rotbMats.whiteBevelMaterial!.clone(), []);
    (progressMaterial as FlatOpaqueTextured).setColor(parseColor(colorSwatch.RankProgress));
    const rankText = useMemo(() => rotbMats.uiTextShadowMaterial!.clone(), []);

    return (
        <Row marginLeft={20} height={160}>
            {/* Portrait */}
            <Container marginTop={20}>
                <ColoredImage
                    position={PositionType.Absolute}
                    src={UiTexturesManager.instance.whiteCircle}
                    color={colorSwatch.MalPortraitBackground}
                    width={160}
                    height={160}
                ></ColoredImage>
                <ColoredImage
                    position={PositionType.Absolute}
                    src={UiTexturesManager.instance.whiteRing}
                    color={colorSwatch.SubTitleBackground}
                    width={160}
                    height={160}
                ></ColoredImage>
                <MalUI
                    top={20}
                    position={PositionType.Absolute}
                    width={160}
                    height={160}
                    additionalRotation={props.additionalRotation}
                ></MalUI>
            </Container>

            <Column marginLeft={100}>
                <Container left={75} height={120}>
                    <MalSpeech
                        width={325}
                        height={120}
                        textWidth={325}
                        direction={TailDirection.left}
                        text={props.text}
                    ></MalSpeech>
                </Container>
                <Container marginTop={20}>
                    <Container>
                        <Panel
                            left={120}
                            top={12}
                            backgroundColor={colorSwatch.RankBackground}
                            rounding={0}
                            width={270}
                            height={50}
                        >
                            <Panel
                                margin={5}
                                backgroundColor={colorSwatch.RankProgressBackground}
                                rounding={0}
                                border={0}
                                width={260}
                                height={40}
                            >
                                <Panel9Slice
                                    margin={0}
                                    material={progressMaterial}
                                    borderTextureSize={0.08}
                                    borderSize={4}
                                    rounding={0}
                                    width={props.xpPercentage * 250}
                                    height={40}
                                ></Panel9Slice>
                                <Text
                                    material={rankText}
                                    top={-34}
                                    marginLeft={28}
                                    fontSize={32}
                                    color={colorSwatch.RankText}
                                    text={props.rank}
                                ></Text>
                            </Panel>
                        </Panel>
                    </Container>

                    <Container position={PositionType.Absolute} top={0}>
                        <ColoredImage
                            z={0.002}
                            left={74}
                            position={PositionType.Absolute}
                            src={UiTexturesManager.instance.whiteCircle}
                            color={colorSwatch.RankBackground}
                            width={74}
                            height={74}
                        >
                            <ColoredImage
                                z={0.001}
                                margin={5}
                                position={PositionType.Absolute}
                                src={UiTexturesManager.instance.whiteCircle}
                                color={colorSwatch.RankProgressBackground}
                                width={64}
                                height={64}
                            >
                                <Image
                                    z={0.001}
                                    marginLeft={10}
                                    marginTop={14}
                                    src={UiTexturesManager.instance.trophyIcon}
                                    rounding={0}
                                    width={44}
                                    height={44}
                                ></Image>
                            </ColoredImage>
                        </ColoredImage>
                    </Container>
                </Container>
            </Column>
        </Row>
    );
};
